/**
 * Game-wide constants. Everything tunable lives here (PRD §4.3, §15).
 */

/** The game's name and home (PRD §15). */
export const GAME_NAME = 'Geosportle';
export const GAME_DOMAIN = 'geosportle.app';

/** Puzzle #1: the day geosportle.app went live. Keep in step with GAME_LAUNCH_DATE (wrangler.jsonc). */
export const LAUNCH_DATE = '2026-10-04';

export const LONDON_TZ = 'Europe/London';
export const MIN_YEAR = 1870;
export const QUESTIONS_PER_ROUND = 5;
/** A round's progress token expires this long after the first question was served (PRD §7). */
export const TOKEN_TTL_MS = 3 * 60 * 60 * 1000;

export const SPORTS = [
  'football',
  'cricket',
  'f1',
  'tennis',
  'rugby',
  'golf',
  'athletics',
  'other',
] as const;

export const QUESTION_STATUSES = ['draft', 'verified', 'live'] as const;

/** Scoring constants (PRD §4.3). Tune here only. */
export const SCORING = {
  /** Maximum "where" points per question. */
  wherePoints: 70,
  /** Maximum "when" points per question. */
  whenPoints: 30,
  /** Exponential decay constant for distance, in km. */
  distanceDecayKm: 2000,
  /** Guesses within this many km get full "where" marks. */
  fullMarksRadiusKm: 10,
  /** "When" points lost per year of error. */
  pointsPerYear: 3,
  /** Multiplier by question position (index 0 = Q1). */
  multipliers: [1, 1, 2, 3, 3],
  /** Mean Earth radius used by the haversine formula. */
  earthRadiusKm: 6371,
} as const;

/** Maximum base (pre-multiplier) score for one question. */
export const MAX_BASE = SCORING.wherePoints + SCORING.whenPoints;

/** Maximum total for a round: 100 × (1 + 1 + 2 + 3 + 3) = 1000. */
export const MAX_TOTAL = SCORING.multipliers.reduce((sum, m) => sum + m * MAX_BASE, 0);

/** Score-distribution bucket width used by daily_stats.histogram_json (PRD §8). */
export const HISTOGRAM_BUCKET = 50;

export const DISPLAY_NAME = { min: 3, max: 16 } as const;
export const GROUP_NAME = { min: 3, max: 24 } as const;
/** Members per group; bounds the reads for a group leaderboard (PRD §8.1). */
export const GROUP_MAX_MEMBERS = 50;
/** Groups a player can belong to. */
export const GROUPS_PER_PLAYER = 10;
/** Device-link codes expire after this long. */
export const LINK_CODE_TTL_MS = 10 * 60 * 1000;

/** Where a pool question came from: the generator, the Google Sheet, or an editor. */
export const POOL_ORIGINS = ['ai', 'sheet', 'editor'] as const;
export const POOL_STATUSES = ['ready', 'scheduled', 'rejected'] as const;

/**
 * Self-filling question bank: dateless questions wait in a pool, and the scheduler assigns
 * them to days ahead of time (PRD §5.2 mix rules), recycling old questions as a last resort.
 */
export const SCHEDULE = {
  /** Days kept scheduled ahead, today included. Also the editor's window to veto a question. */
  horizonDays: 14,
  /** The generator tops the pool up to this many unscheduled questions (two more weeks). */
  poolTarget: 70,
  /** The same venue is not used again within this many days either side. */
  venueCooldownDays: 30,
  /** A recycled question should not have been shown for this long (relaxed when nothing is). */
  recycleAfterDays: 365,
  /** With at least this many plays, a question's difficulty comes from real scores. */
  minPlaysForObservedDifficulty: 10,
  /** Questions averaging fewer base points than this are never recycled (likely broken). */
  retireBelowAvg: 8,
  /** Two venues closer than this (one city) don't share a day when it can be avoided. */
  sameDayGapKm: 25,
  /** Distinct continents a day should cover when the pool allows it (PRD §5.2). */
  minContinents: 3,
} as const;

/** Reasons a player can pick when reporting a question from the answer card. */
export const REPORT_REASONS = {
  place: 'Wrong place',
  year: 'Wrong year',
  clue: 'Clue is unclear or gives it away',
  other: 'Something else',
} as const;
export type ReportReason = keyof typeof REPORT_REASONS;
export const REPORT_NOTE_MAX = 300;
